import { computed, ComputedRef, ref, Ref, ShallowRef, shallowRef, watch, watchEffect } from "vue";
import { acceptHMRUpdate, defineStore } from "pinia";
import { computedAsync, watchThrottled, watchTriggerable } from "@vueuse/core";
import useUtils from "@/shared/composables/use-utils";
import useOpenbetApi from "@/api/composables/use-openbet-api";
import type { Outcome, OutcomeDetailsBet, OutcomeMarket } from "@/shared/model/outcome";
import { EmptyMarket, Market } from "@/shared/model/market";
import { EventsDrilldownDetailEventMarketOutcome } from "@/live/model/events-drilldown-detail-event";
import { EventDetail } from "@/shared/model/event";
import {
    errorDictionary,
    KombiGroup,
    KombiGroupMapping,
    PriceChangeBehaviour,
    SimplifiedOutcome,
    TicketType,
    WarningFlag,
    WarningFlagTarget,
    WarningFlagType,
} from "@/shared/model/ticket";
import {
    cloneDeep as _cloneDeep,
    differenceWith as _differenceWith,
    isEmpty as _isEmpty,
    isEqual as _isEqual,
    isFinite as _isFinite,
    isNumber as _isNumber,
    omit as _omit,
    orderBy as _orderBy,
    unionBy as _unionBy,
    uniqBy as _uniqBy,
    xor as _xor,
} from "lodash";
import { Leg } from "@/shared/model/leg";
import { SlipBuild } from "@/api/interfaces/slip-build";
import { addMapping, removeMapping } from "@/shared/utils/mapping";
import { Price as _Price, PriceSlipResponse } from "@/shared/model/price";
import { Status } from "@/shared/model/enums/status";
import { TicketError } from "@/ticket/model/ticket-error";
import useConfirmModal from "@/shared/composables/use-confirm-modal";
import { SlipBet, SlipBetData, SlipBetStatus } from "@/api/interfaces/slip-bet";
import { TicketSending } from "@/ticket/model/ticket-sending";
import useTokenAuth from "@/shared/composables/use-token-auth";
import { useRoute } from "vue-router";
import { useFavouritesStore } from "@/shared/stores/use-favourites-store";
import { useNgSession } from "@/shared/ng/ng-session";
import { Bet, SystemBet } from "@/shared/model/bet";
import { SubType1Letter, SubType2Letter, Type } from "@/shared/model/enums/type";
import { useAuthStore } from "@/shared/stores/use-auth-store";
import { BetCategory, BetLabel, BetPlacement } from "@/shared/model/enums/analytics/bet";
import analytics from "@/shared/analytics";
import useAnalyticsUtils from "@/shared/composables/analytics/use-analytics-utils";
import { useDrilldownNodesStore } from "@/shared/stores/use-drilldown-nodes-store";
import { logCustomException, logException } from "@/shared/utils/logger/use-app-logger";
import { ngFrameworkLoaded } from "@/shared/ng/ng-integration";
import { useBetsStore } from "@/shared/stores/use-bets-store";
import { wait, waitUntilTrue } from "@/shared/utils/wait";
import PQueue from "p-queue";
import { StateEnum } from "@/api/stores/use-api-store";
import { useCookieStorage } from "@/shared/composables/use-storage";
import { isDevelopmentEnv } from "@/shared/utils/environment-variables";
import useSazkaSnackbar from "@/shared/composables/use-sazka-snackbar";
import { analyticsTicketObj } from "@/shared/composables/analytics/use-general-entities";
import { SortCode } from "@/shared/model/enums/sort-code";
import { Commentary } from "@/shared/model/commentary";
import { GroupCode } from "@/shared/model/enums/group-code";
import useIsInitialPageLoad from "@/shared/composables/use-is-initial-page-load";

export const handleClickCapture = (event: Event) => {
    const ticketStore = useTicketStore();
    if (ticketStore.sendingInProgress) {
        event.preventDefault();
        event.stopPropagation();
        ticketStore.userAttemptedToUpdateTicketDuringSending = true;
    }
};

export const normalizeGroupNumber = (group: string) =>
    _isNumber(Number(group)) && _isFinite(Number(group)) ? group : "0";

const setFlag = (
    flags: WarningFlag[],
    target: WarningFlagTarget,
    type: WarningFlagType,
    id: string | string[],
    text?: string,
) => {
    const flagIdx = flags.findIndex((flag) => flag.target === target && flag.type === type);
    if (flagIdx > -1) {
        flags[flagIdx] = {
            ...flags[flagIdx],
            info: [...flags[flagIdx].info, id],
        };
    } else {
        flags.push({
            target: target,
            type: type,
            info: [id],
            text: text,
        });
    }
};

// We need to maintain group number consistency (numbers needs to follow each other without gaps)
const repairKombiGroupNumberMappingConsistency = (mapping: KombiGroupMapping) => {
    const newMapping = _cloneDeep(mapping);
    const consistentMapping: KombiGroupMapping = {};
    if (newMapping[KombiGroup.T]) {
        consistentMapping[KombiGroup.T] = newMapping[KombiGroup.T];
    }
    for (let i = 1; i <= MAX_KOMBIGROUP_NUMBER; i++) {
        if (newMapping[i]) {
            consistentMapping[Object.keys(consistentMapping).length + (newMapping[KombiGroup.T] ? 0 : 1)] =
                newMapping[i];
        }
    }
    return consistentMapping;
};

// Input date time string should be 'YYYY-MM-DDTHH:mm:ss.sssZ' (simplification of the ISO 8601 calendar date extended format)
const isPast = (dateTime: string): boolean => Date.parse(dateTime) < Date.now();

export const MAX_TICKET_NUMBER: number = 1; // Future preparation for multiple tickets (just increase the number)... Would be much more complicated in future to implement support for multiple tickets feature. Unnecessarily complicated if we'll stay with single ticket but can save our asses in the future. :)
export const MAX_KOMBIGROUP_NUMBER: number = 10; // KombiGroups A-J (T is not counted as group)
export const MAX_LEG_NUMBER: number = 30;
export const MIN_STAKE = 10;
export const MIN_KOMBI_STAKE = 0.01;
const AVAILABLE_KOMBIGROUP_ALL = false; // Determine whether display all available kombi groups or just subset (current group number + 1)
const TICKET_STAKE: number = 100;
// TODO: Evaluate whether we need to fetch fresh data in such rapid rate.
//  30s should be enough when we have websocket implemented (maybe because of detection of finished events, dunno... need to investigate).
//  Keep in mind that meanwhile currentActiveOffset was implemented for football and is updated by middleware only (no websocket updates)... so it seems that fetch interval will stay as it is for a while.
const FETCH_INTERVAL: number = 10000;
const RESTORE_EXPIRATION = 4 * 60 * 60 * 1000; // 4 hrs

export const transformToOutcomeMarket = (odb: OutcomeDetailsBet): OutcomeMarket => {
    // no data - means we have no data or substitute in source object hence setting some default (should not matter if used in addOutcome() or toggleOutcome())
    return {
        id: odb.id,
        name: odb.name,
        marketId: odb.marketRef,
        active: true, // no data
        status: Status.Active, // no data
        displayed: true, // no data
        displayOrder: 0, // no data
        type: SubType2Letter.Empty, // no data
        subType: SubType1Letter.A, // no data
        prices: odb.prices as unknown as _Price[],
        empty: false,
        parentMarket: {
            handicapValue: 0, // no data
            eventId: odb.eventRef,
            active: true, // no data
            status: Status.Active, // no data
        },
    };
};

export const transformToEventDetail = (odb: OutcomeDetailsBet): EventDetail => {
    const transformedOutcome = transformToOutcomeMarket(odb) as Outcome;
    return {
        active: !odb?.result,
        commentary: {} as Commentary, // no data
        competitionDrilldownTagId: odb.typeRef,
        collections: [], // no data
        displayOrder: 0, // no data
        displayed: true, // no data
        externalIds: [], // no data
        id: odb.eventRef,
        liveNow: false, // no data
        marketCounts: { total: 1 },
        markets: [
            {
                active: true, // no data
                betInRun: false, // no data
                betInRunIndex: 0, // no data
                displayOrder: 0, // no data
                displayed: true, // no data
                eventId: odb.eventRef,
                groupCode: GroupCode.MatchWinner, // no data
                handicapValue: 0, // no data
                id: odb.marketRef,
                name: odb.marketName,
                outcomes: [transformedOutcome],
                status: Status.Active, // no data
                subType: SubType2Letter.Empty, // no data
                type: Type.Empty, // no data
            },
        ],
        mediaProviders: [], // no data
        name: odb.eventName,
        resulted: !!odb?.result,
        sortCode: SortCode.Match, // no data
        startTime: odb.startDate,
        started: isPast(odb.startDate),
        status: Status.Active, // no data
        teams: [], // no data
    };
};

// Wrapper store to handle multiple tickets which are separated to multiple stores. Only one store/ticket can be active (driven by activeStoreNumber).
// Maybe a little bit overcomplicated but prepared for future if there will be request for multiple ticket support (as on Tipsport)... Can be simplified in the future if there will be no such request from business.
export const useTicketStore = defineStore(
    "Ticket",
    () => {
        const activeStoreNumber: Ref<number> = ref(1);
        const stores = ref({});
        const errors: Ref<TicketError[]> = ref<TicketError[]>([]);
        const showSendingErrors: Ref<boolean> = ref(false);
        const userAttemptedToUpdateTicketDuringSending: Ref<boolean> = ref(false);

        // Initialize number of stores based on MAX_TICKET_NUMBER
        for (let i = 1; i <= MAX_TICKET_NUMBER; i++) {
            stores.value[i] = useTicketBaseStore(i)();
            // Enable HMR
            if (import.meta.hot) {
                import.meta.hot.accept(acceptHMRUpdate(useTicketBaseStore(i), import.meta.hot));
            }
        }
        const activeStore = computed(() => stores.value[activeStoreNumber.value]);
        const initialized: ComputedRef<boolean> = computed(() => activeStore.value?.initialized);
        const sendingInProgress: ComputedRef<TicketSending | undefined> = computed(
            () => activeStore.value?.sendingInProgress,
        );
        const eventsFiltered: ComputedRef<EventDetail[]> = computed(() => activeStore.value?.actualEvents);
        const ticketType: ComputedRef<TicketType> = computed(() => activeStore.value?.ticketType);
        // solo/ako stake, should be renamed to something more suitable soloAkoStake or something like that
        const ticketStake: ComputedRef<number> = computed(() => activeStore.value?.ticketStake);
        // Outcome values at the time when they were added to ticket
        const originalOutcomes: ComputedRef<Array<OutcomeMarket | EventsDrilldownDetailEventMarketOutcome>> = computed(
            () => activeStore.value?.outcomes || [],
        );

        const ticketOutcomes: ComputedRef<Outcome[]> = computed(() => activeStore.value?.actualOutcomes || []);
        const ticketOutcomeCount: ComputedRef<number> = computed(() => activeStore.value?.actualOutcomes?.length || 0);
        // For some unknown reason ep sometimes does not return bets[] even status is "BUILD" and without any errors
        const bet: ComputedRef<Bet> = computed(() => activeStore.value?.slipBuild?.data?.bets?.[0]);
        const systemBets: ComputedRef<SystemBet[]> = computed(() => bet.value?.systemBets);
        // kombi stakes for various groups
        const kombiStakes: ComputedRef<number[]> = computed(() => activeStore.value?.kombiStakes || []);
        // kombi overall stake calculated based on number of groups and created bets
        const kombiStakesTotal: ComputedRef<number> = computed(
            () =>
                systemBets.value?.reduce((count: number, sb, idx) => {
                    return count + (sb.lines * kombiStakes.value?.[idx + 1] || 0);
                }, 0) || 0,
        );
        // Overall stake
        const stake: ComputedRef<number> = computed(() =>
            ticketType.value === TicketType.KOMBI ? kombiStakesTotal.value : ticketStake.value,
        );
        const potentialPayout: ComputedRef<number> = computed(() => Number(bet.value?.potentialPayout[0].value || 0)); // value is null if KOMBI ticket
        const bonusPercent: ComputedRef<number> = computed(
            () => activeStore.value?.slipBuild?.data?.bonuses?.[0]?.value || 0,
        );
        const bonus: ComputedRef<number> = computed(() =>
            bonusPercent.value ? ticketStake.value * potentialPayout.value * (bonusPercent.value / 100) : 0,
        );
        const kombiPossibleWin: ComputedRef<number> = computed(
            () =>
                systemBets.value?.reduce((count: number, sb, idx) => {
                    return count + (Number(sb.potentialPayout[0].value) * kombiStakes.value[idx + 1] || 0);
                }, 0) || 0,
        );
        const soloAkoPossibleWin: ComputedRef<number> = computed(
            () => ticketStake.value * potentialPayout.value + bonus.value,
        );
        const possibleWin: ComputedRef<number> = computed(() =>
            ticketType.value === TicketType.KOMBI ? kombiPossibleWin.value : soloAkoPossibleWin.value,
        );
        const warningFlags: ComputedRef<WarningFlag[]> = computed(() => {
            const flags: WarningFlag[] = [];
            // There are some warning flags which we need to filter out based on users selected optionPriceChangeBehaviour value
            activeStore.value?.warningFlags.forEach((flag: WarningFlag) => {
                if (
                    flag.type === "BET_PRICE_CHANGED" &&
                    (flag.target === "ticket" || optionPriceChangeBehaviour.value === PriceChangeBehaviour.ALL)
                ) {
                    // We'll ignore the flag here
                    // "ticket" BET_PRICE_CHANGED errors should be ignored... they are "useless" and appear before placing the bet (we'll not react on them, they are just informing us that we have outdated data in comparison with /build ep)
                    // "bet" BET_PRICE_CHANGED errors will appear only during creation process of the bet and should be shown to user
                    // if priceChangeBehaviour is set to ALL, api should accept any changes during creation process and no errors should appear... hope that's true
                } else if (
                    flag.type === "BET_PRICE_CHANGED" &&
                    flag.target === "bet" &&
                    optionPriceChangeBehaviour.value === PriceChangeBehaviour.HIGHER
                ) {
                    // /slips ep (apply to /build ep as well) is so poorly written that it returns all "BET_PRICE_CHANGED" errors even if we explicitly set to accept HIGHER so additional filtering is needed here
                    const filteredFlagInfo = flag.info.filter((flagInfo) => {
                        const documentId = flagInfo[1];
                        const slipPrice = slipBuild.value?.data?.legs.find((leg: Leg) => leg.documentId === documentId)
                            ?.options.prices?.[0] as unknown as PriceSlipResponse;
                        const betPrice = slipBet.value?.data?.legs.find((leg: Leg) => leg.documentId === documentId)
                            ?.options.prices?.[0] as unknown as PriceSlipResponse;
                        return slipPrice?.decimal >= betPrice?.decimal;
                    });
                    flags.push({ ...flag, info: filteredFlagInfo });
                } else {
                    flags.push(flag);
                }
            });

            if (showSendingErrors.value && !empty.value && stake.value < MIN_STAKE) {
                flags.push({ target: "ticket", type: "TRANSACTION_TOTAL_TOO_LOW", info: [stake.value.toString()] });
            }
            if (
                showSendingErrors.value &&
                !empty.value &&
                ticketType.value === TicketType.KOMBI &&
                kombiStakes.value.slice(1).some((stake: number) => stake < MIN_KOMBI_STAKE && stake)
            ) {
                flags.push({
                    target: "ticket",
                    type: "TRANSACTION_KOMBI_TOO_LOW",
                    info: kombiStakes.value.slice(1).map((stake: number, idx) => {
                        if (!stake || stake < MIN_KOMBI_STAKE) return idx.toString();
                    }),
                });
            }
            if (userAttemptedToUpdateTicketDuringSending.value && !empty.value) {
                flags.push({
                    target: "ticket",
                    type: "USER_TICKET_CHANGE_DURING_SENDING",
                    info: [String(sendingInProgress.value)],
                });
            }
            return flags;
        });
        // Some errors are ignored in wrapper store, so the value can differ from 'base' store
        const isWarningRaised: ComputedRef<boolean> = computed(() => warningFlags.value.length > 0);
        const isErrorRaised: ComputedRef<boolean> = computed(
            () => errors.value?.filter((err) => !err.dismissed)?.length > 0,
        );
        const availableKombiGroups: ComputedRef<KombiGroup[]> = computed(() => activeStore.value?.availableKombiGroups);
        const kombiGroupNumberByOutcomeId: ComputedRef<(outcomeId: string) => number> = computed(
            () => (outcomeId: string) => activeStore.value?.kombiGroupNumberByOutcomeId(outcomeId),
        );
        const availableKombiGroupsByOutcomeId: ComputedRef<(outcomeId: string) => number[]> = computed(
            () => (outcomeId: string) => activeStore.value?.availableKombiGroupsByOutcomeId(outcomeId),
        );

        const empty = computed<boolean>(() => activeStore.value?.empty);
        const placed = computed<boolean>(() => activeStore.value?.placed);
        const kombiGroupMapping: ComputedRef<KombiGroupMapping> = computed(() => activeStore.value?.kombiGroupMapping);
        const slipBuild: ComputedRef<SlipBuild> = computed(() => activeStore.value?.slipBuild);
        const slipBet: ComputedRef<SlipBet> = computed(() => activeStore.value?.slipBet);
        const eventIds: ComputedRef<string[]> = computed(() => activeStore.value?.eventIds || []);
        const outcomeIds: ComputedRef<string[]> = computed(() => activeStore.value?.outcomeIds || []);
        const optionDisplayMiniTicket: Ref<boolean> = ref(true); // persisted
        const optionPriceChangeBehaviour: Ref<PriceChangeBehaviour> = ref(PriceChangeBehaviour.NONE); // persisted
        const pinned: Ref<boolean> = ref(true); // persisted
        const settingsOpen: Ref<boolean> = ref(false); // persisted
        const collapsed: Ref<boolean> = ref(false); // persisted

        const setActiveStoreNumber = (number: number) => {
            activeStoreNumber.value = number;
        };
        const setTicketType = (type: TicketType) => {
            activeStore.value?.setTicketType(type);
        };
        const getEventByOutcomeId = (id: string): EventDetail => {
            return activeStore.value?.getEventByOutcomeId(id);
        };
        const changeStake = (amount: number, groupNumber?: number) => {
            activeStore.value?.changeStake(amount, groupNumber);
        };
        const setKombiGroupMapping = (outcomeId: string, groupNumber: KombiGroup) => {
            activeStore.value?.setKombiGroupMapping(outcomeId, groupNumber);
        };
        const replaceKombiGroupMapping = (mapping: KombiGroupMapping) => {
            activeStore.value?.replaceKombiGroupMapping(mapping);
        };
        const isOutcomePresent = (outcome: OutcomeMarket | EmptyMarket | EventsDrilldownDetailEventMarketOutcome) => {
            return activeStore.value?.isOutcomePresent(outcome);
        };
        const userUpdateWhileSendingGuard = () => {
            if (sendingInProgress.value) {
                userAttemptedToUpdateTicketDuringSending.value = true;
                return true;
            }
            return false;
        };
        const toggleOutcome = (
            outcome: OutcomeMarket | EmptyMarket | EventsDrilldownDetailEventMarketOutcome,
            betPlacement: BetPlacement = BetPlacement.Main,
            betCategory: BetCategory = BetCategory.Sport,
        ) => {
            if (userUpdateWhileSendingGuard()) return;
            activeStore.value?.toggleOutcome(outcome, betPlacement, betCategory);
        };
        const addOutcome = (outcome: OutcomeMarket | EmptyMarket | EventsDrilldownDetailEventMarketOutcome) => {
            if (userUpdateWhileSendingGuard()) return;
            activeStore.value?.addOutcome(outcome);
        };
        const removeOutcome = (outcomeId: string) => {
            if (userUpdateWhileSendingGuard()) return;
            activeStore.value?.removeOutcome(outcomeId);
        };
        const removeOutcomes = (ids: string[]) => {
            if (userUpdateWhileSendingGuard()) return;
            ids.forEach((id) => removeOutcome(id));
        };
        const sendTicket = async () => {
            clearSlipBet();
            await waitUntilTrue(() => !isWarningRaised.value, 100, 1000).catch(() => {
                logCustomException(
                    "ticket-store",
                    "Waiting for warning deletion timed out. Timeout interval 1000 breached.",
                );
            });
            showSendingErrors.value = true;
            if (isWarningRaised.value) {
                // Shouldn't happen that user is able to place 'invalid' ticket
                logCustomException("use-ticket-store_sendTicket_invalid", "There is something wrong with the ticket!", {
                    slipBuild: slipBuild.value,
                    slipBet: slipBet.value,
                    warningFlags: warningFlags.value,
                });
                return;
            }
            try {
                await activeStore.value?.sendTicket(optionPriceChangeBehaviour.value);
            } catch (error) {
                logException("ticket-store", error, {
                    message: "An error has occurred while sending ticket",
                    slipBuild: slipBuild.value,
                });
            }
            showSendingErrors.value = false;
        };
        const clearTicket = (confirm: boolean = true) => {
            if (!confirm) {
                activeStore.value?.clearTicket();
                return;
            }

            const { openModal } = useConfirmModal();

            openModal({
                title: "Smazat tiket?",
                text: "Všechny příležitosti budou z tiketu odstraněny.",
                cancelText: "Zrušit",
                submitText: "Smazat",
                submitAction: () => {
                    activeStore.value?.clearTicket();
                },
            });
        };
        const clearSlipBet = () => {
            activeStore.value?.clearSlipBet();
        };

        const setOptionDisplayMiniTicket = (val: boolean) => {
            optionDisplayMiniTicket.value = val;
        };

        const setOptionPriceChangeBehaviour = (val: PriceChangeBehaviour) => {
            optionPriceChangeBehaviour.value = val;
        };

        const dismissError = (id: string) => {
            const err = errors.value.find((error) => error.id === id);
            err.dismissed = true;
        };

        const addEventToBackup = (evt: EventDetail) => {
            activeStore.value?.addEventToBackup(evt);
        };

        watch(activeStoreNumber, (newValue, oldValue) => {
            if (newValue) stores.value[newValue].setFetchInterval(FETCH_INTERVAL);
            if (oldValue) stores.value[oldValue].setFetchInterval(0);
        });

        // Watcher which is responsible for creation of error messages and their actions based on warning flags.
        // This became a horrible piece of code over time. :(
        watch(
            [() => warningFlags.value, () => optionPriceChangeBehaviour.value],
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            ([newFlags, newOptionPriceChangeBehaviour], [oldErrors, oldOptionPriceChangeBehaviour]) => {
                let newErrors = _cloneDeep(errors.value);
                if (!newFlags?.length) {
                    newErrors = [];
                }
                // We are primary interested in ticket/bet flags(errors)... the rest we need to calculate from other flags
                const newTicketAndBetFlags = newFlags.filter((ne) => ["ticket", "bet"].includes(ne.target));

                const missingEventIds = (newFlags?.find((flag) => flag.target === "event" && flag.type === "missingIds")
                    ?.info || []) as string[];
                const missingOutcomeIds = (newFlags?.find(
                    (flag) => flag.target === "outcome" && flag.type === "missingIds",
                )?.info || []) as string[];
                const suspendedTicketOutcomeIds = (newFlags
                    ?.find((flag) => flag.target === "ticket" && flag.type === "OUTCOME_SUSPENDED")
                    ?.info?.map((i) => i?.[0]) || []) as string[];
                const startedTicketOutcomeIds = (newFlags
                    ?.find((flag) => flag.target === "ticket" && flag.type === "EVENT_STARTED")
                    ?.info?.map((i) => i?.[0]) || []) as string[];
                const suspendedOutcomeIds = (newFlags?.find(
                    (flag) => flag.target === "outcome" && flag.type === "suspended",
                )?.info || []) as string[];
                const suspendedEventIds = (newFlags?.find(
                    (flag) => flag.target === "event" && flag.type === "suspended",
                )?.info || []) as string[];

                // Clean up errors which are not valid anymore
                newErrors = newErrors.filter((e) =>
                    newTicketAndBetFlags.some((ne) => !_differenceWith(ne.info, e.errorRefs, _isEqual).length),
                );
                // Re-evaluate "BET_PRICE_CHANGED" errors if the option changes
                if (newOptionPriceChangeBehaviour !== oldOptionPriceChangeBehaviour) {
                    newErrors = newErrors.filter((e) => e.errorId !== "BET_PRICE_CHANGED");
                }

                for (const [index, flag] of newTicketAndBetFlags?.entries() || []) {
                    const id = `${index}-${flag.type}`;
                    const errorId = `${flag.type}`;

                    let ticketError: TicketError;
                    switch (flag.type) {
                        case "OUTCOME_SUSPENDED": {
                            const flagInfo = flag.info.filter(
                                (i) => !(missingOutcomeIds.includes(i[0]) && missingEventIds.includes(i[3])),
                            );
                            ticketError = {
                                id,
                                errorId,
                                errorRefs: flagInfo,
                                title: "Upozornění",
                                message: errorDictionary[flag.type] || flag.text,
                                buttons: [
                                    {
                                        text: "Odstranit všechny pozastavené",
                                        action: () => {
                                            removeOutcomes(flagInfo.map((i) => i[0]));
                                            clearSlipBet();
                                        },
                                    },
                                ],
                                dismissed: false,
                            };
                            break;
                        }
                        // We should behave to "EVENT_STARTED" like to "OUTCOME_SUSPENDED", even the textation should be the same (defined by business)
                        case "EVENT_STARTED":
                            ticketError = {
                                id,
                                errorId,
                                errorRefs: flag.info,
                                title: "Upozornění",
                                message: errorDictionary[flag.type] || flag.text,
                                buttons: [
                                    {
                                        text: "Odstranit všechny pozastavené",
                                        action: () => {
                                            removeOutcomes(flag.info.map((i) => i[0]));
                                            clearSlipBet();
                                        },
                                    },
                                ],
                                dismissed: false,
                            };
                            break;
                        case "BET_PRICE_CHANGED": {
                            const flagInfo = flag.info.filter(
                                (i) =>
                                    !suspendedTicketOutcomeIds.includes(i[0]) &&
                                    !(missingOutcomeIds.includes(i[0]) || missingEventIds.includes(i[3])),
                            );
                            ticketError = {
                                id,
                                errorId,
                                errorRefs: flagInfo,
                                title: "Upozornění",
                                message: errorDictionary[flag.type] || flag.text,
                                buttons: [
                                    {
                                        text: "Přijmout všechny změny",
                                        action: () => {
                                            activeStore.value.buildSlip(true);
                                            clearSlipBet();
                                        },
                                    },
                                ],
                                dismissed: false,
                            };
                            break;
                        }
                        case "TRANSACTION_KOMBI_TOO_LOW":
                        case "TRANSACTION_TOTAL_TOO_LOW":
                            ticketError = {
                                id,
                                errorId,
                                errorRefs: flag.info,
                                title: "Upozornění",
                                message: errorDictionary[flag.type] || flag.text,
                                buttons: [
                                    {
                                        text: "Rozumím",
                                        action: () => {
                                            const transactionErrors =
                                                newErrors?.filter((e) =>
                                                    ["TRANSACTION_KOMBI_TOO_LOW", "TRANSACTION_TOTAL_TOO_LOW"].includes(
                                                        e.errorId,
                                                    ),
                                                ) || [];
                                            const nonDismissedCount =
                                                transactionErrors?.filter((e) => !e.dismissed)?.length || 0;
                                            if (nonDismissedCount <= 1) {
                                                showSendingErrors.value = false;
                                            }
                                            dismissError(id);
                                        },
                                    },
                                ],
                                dismissed: false,
                            };
                            break;
                        case "COOL_OFF_LIMIT":
                        case "ACCOUNT_IRRESPONSIBLE_GAMING":
                        case "ACCOUNT_EXCEEDED_DAY_LOSS_LIMIT":
                        case "ACCOUNT_EXCEEDED_MONTH_LOSS_LIMIT":
                            ticketError = {
                                id,
                                errorId,
                                errorRefs: flag.info,
                                title: "Upozornění",
                                message: errorDictionary[flag.type] || flag.text,
                                buttons: [
                                    {
                                        text: "Moje limity",
                                        action: () => {
                                            window.open(
                                                `${environment.portalBaseUrl}?rg-limits=true`,
                                                "_blank",
                                                "noopener,noreferrer",
                                            );
                                        },
                                    },
                                    {
                                        text: "Rozumím",
                                        action: () => {
                                            const transactionErrors =
                                                newErrors?.filter((e) =>
                                                    [
                                                        "COOL_OFF_LIMIT",
                                                        "ACCOUNT_IRRESPONSIBLE_GAMING",
                                                        "ACCOUNT_EXCEEDED_DAY_LOSS_LIMIT",
                                                        "ACCOUNT_EXCEEDED_MONTH_LOSS_LIMIT",
                                                    ].includes(e.errorId),
                                                ) || [];
                                            const nonDismissedCount =
                                                transactionErrors?.filter((e) => !e.dismissed)?.length || 0;
                                            if (nonDismissedCount <= 1) {
                                                showSendingErrors.value = false;
                                            }
                                            dismissError(id);
                                        },
                                    },
                                ],
                                dismissed: false,
                            };
                            break;
                        case "INSUFFICIENT_FUNDS":
                            ticketError = {
                                id,
                                errorId,
                                errorRefs: flag.info,
                                title: "Upozornění",
                                message: errorDictionary[flag.type] || flag.text,
                                buttons: [
                                    {
                                        text: "Vložit peníze",
                                        action: async () => {
                                            try {
                                                await ngFrameworkLoaded();
                                                ng_fw.showDeposit();
                                            } catch (e) {
                                                console.error(e);
                                            }
                                        },
                                    },
                                    {
                                        text: "Rozumím",
                                        action: () => dismissError(id),
                                    },
                                ],
                                dismissed: false,
                            };
                            break;
                        case "USER_TICKET_CHANGE_DURING_SENDING":
                            ticketError = {
                                id,
                                errorId,
                                errorRefs: flag.info,
                                title: "Upozornění",
                                message: errorDictionary[flag.type] || flag.text,
                                buttons: [
                                    {
                                        text: "Rozumím",
                                        action: () => (userAttemptedToUpdateTicketDuringSending.value = false),
                                    },
                                ],
                                dismissed: false,
                            };
                            break;
                        default:
                            ticketError = {
                                id,
                                errorId,
                                errorRefs: flag.info,
                                title: "Upozornění",
                                message: errorDictionary[flag.type] || flag.text,
                                buttons: [
                                    {
                                        text: "Rozumím",
                                        action: () => dismissError(id),
                                    },
                                ],
                                dismissed: false,
                            };
                    }
                    // Aggregate "EVENT_STARTED" and "OUTCOME_SUSPENDED" warnings into single "OUTCOME_SUSPENDED". Why? Because business wants it like that.
                    if (["EVENT_STARTED", "OUTCOME_SUSPENDED"].includes(ticketError.errorId)) {
                        const suspendedError = newErrors.find((error) => error.errorId === "OUTCOME_SUSPENDED");
                        if (suspendedError) {
                            suspendedError.errorRefs = _uniqBy(
                                [...ticketError.errorRefs, ...suspendedError.errorRefs],
                                (a, b) => a?.[0] === b?.[0],
                            );
                            continue;
                        } else if (ticketError.errorId === "EVENT_STARTED") {
                            ticketError.id = `${index}-OUTCOME_SUSPENDED`;
                            ticketError.errorId = "OUTCOME_SUSPENDED";
                        }
                    }
                    if (newErrors.some((error) => error.errorId === errorId) || !ticketError.errorRefs.length) continue;

                    newErrors.push(ticketError);
                }

                const nonCombinableLegGroupsFlagInfo = (newFlags?.find(
                    (flag) => flag.target === "leg" && flag.type === "nonCombinableLegGroups",
                )?.info || []) as string[];
                const nonCombinableLegGroupsError = newErrors.find((e) => e.errorId === "nonCombinableLegGroups");
                if (
                    (!nonCombinableLegGroupsError && nonCombinableLegGroupsFlagInfo?.length > 0) ||
                    _differenceWith(
                        nonCombinableLegGroupsError?.errorRefs || [],
                        nonCombinableLegGroupsFlagInfo || [],
                        _isEqual,
                    ).length > 0
                ) {
                    const errorId = "nonCombinableLegGroups";
                    const ticketError = {
                        id: `${newErrors.length}-${errorId}`,
                        errorId,
                        errorRefs: nonCombinableLegGroupsFlagInfo,
                        title: "Upozornění",
                        message:
                            "Na tiket byly přidány zápasy, které nelze kombinovat. Před vsazením některý z nich odstraňte.",
                        buttons: [],
                        dismissed: false,
                    };
                    if (!nonCombinableLegGroupsError) {
                        newErrors.push(ticketError);
                    } else {
                        nonCombinableLegGroupsError.errorRefs = ticketError.errorRefs;
                        nonCombinableLegGroupsError.buttons = ticketError.buttons;
                        nonCombinableLegGroupsError.dismissed = ticketError.dismissed;
                    }
                }

                // Resulted events are based on suspended events which does not have event and outcome returned from api
                const resultedFlagInfo = newFlags
                    ?.find((flag) => flag.target === "ticket" && flag.type === "OUTCOME_SUSPENDED")
                    ?.info.filter(
                        (i) =>
                            missingOutcomeIds.includes(i[0]) &&
                            missingEventIds.includes(i[3]) &&
                            !startedTicketOutcomeIds.includes(i[0]),
                    );
                const resultedError = newErrors.find((e) => e.errorId === "OUTCOME_RESULTED");
                if (
                    (!resultedError && resultedFlagInfo?.length > 0) ||
                    _differenceWith(resultedError?.errorRefs || [], resultedFlagInfo || [], _isEqual).length > 0
                ) {
                    const errorId = "OUTCOME_RESULTED";
                    const ticketError = {
                        id: `${newErrors.length}-${errorId}`,
                        errorId,
                        errorRefs: resultedFlagInfo,
                        title: "Upozornění",
                        message: "Před vsazením prosím odstraňte z tiketu nedostupné příležitosti.",
                        buttons: [
                            {
                                text: "Odstranit všechny nedostupné",
                                action: () => removeOutcomes(resultedFlagInfo.map((i) => i[0])),
                            },
                        ],
                        dismissed: false,
                    };
                    if (!resultedError) {
                        newErrors.push(ticketError);
                    } else {
                        resultedError.errorRefs = ticketError.errorRefs;
                        resultedError.buttons = ticketError.buttons;
                        resultedError.dismissed = ticketError.dismissed;
                    }
                }

                // We need to extend "OUTCOME_SUSPENDED" error with data from api/websocket when outcome/event become suspended
                if (suspendedEventIds.length > 0 || suspendedOutcomeIds.length > 0) {
                    const suspendedError = newErrors.find((e) => e.errorId === "OUTCOME_SUSPENDED");
                    const errorId = "OUTCOME_SUSPENDED";
                    const suspendedFlagInfo = newFlags
                        ?.find((flag) => flag.target === "ticket" && flag.type === "OUTCOME_SUSPENDED")
                        ?.info.filter((i) => !(missingOutcomeIds.includes(i[0]) || missingEventIds.includes(i[3])));
                    const flagInfo = [
                        ...(suspendedFlagInfo || []),
                        ...(suspendedOutcomeIds
                            ?.filter((id) => !suspendedFlagInfo?.map((i) => i[0])?.includes(id))
                            ?.map((id) => [
                                id,
                                undefined,
                                undefined,
                                originalOutcomes.value.find((oo) => oo.id === id)?.parentMarket.eventId,
                            ]) || []),
                        ...(suspendedEventIds
                            ?.filter((id) => !suspendedFlagInfo?.map((i) => i[3])?.includes(id))
                            ?.map((id) => [
                                originalOutcomes.value.find((oo) => oo?.parentMarket.eventId === id)?.id,
                                undefined,
                                undefined,
                                id,
                            ]) || []),
                    ];
                    const ticketError = {
                        id: `${newErrors.length}-${errorId}`,
                        errorId,
                        errorRefs: flagInfo,
                        title: "Upozornění",
                        message: errorDictionary[errorId],
                        buttons: [
                            {
                                text: "Odstranit všechny pozastavené",
                                action: () => removeOutcomes(flagInfo.map((i) => i[0])),
                            },
                        ],
                        dismissed: false,
                    };
                    if (!suspendedError) {
                        newErrors.push(ticketError);
                    } else {
                        suspendedError.errorRefs = ticketError.errorRefs;
                        suspendedError.buttons = ticketError.buttons;
                        suspendedError.dismissed = ticketError.dismissed;
                    }
                }
                errors.value = newErrors;
            },
            { immediate: true },
        );
        watch(placed, (newValue) => {
            if (newValue) {
                showSendingErrors.value = false;
                userAttemptedToUpdateTicketDuringSending.value = false;
            }
        });
        watch(empty, (newValue, oldValue) => {
            if (newValue && !oldValue) {
                showSendingErrors.value = false;
                userAttemptedToUpdateTicketDuringSending.value = false;
            }
        });
        // Cleanup mechanism which should remove all resulted outcomes on next page visit
        const { isInitialPageLoad } = useIsInitialPageLoad(); // Value should be calculated on store init (don't move anywhere else)
        const unwatch = watch(
            [() => activeStore.value?.initialized, () => errors.value],
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            ([newInitialized, newErrors], [oldInitialized, oldErrors]) => {
                if (newInitialized && newErrors.length > 0 && oldErrors.length === 0) {
                    const resultedOutcomeIds =
                        newErrors?.find((err) => err.errorId === "OUTCOME_RESULTED")?.errorRefs?.map((i) => i?.[0]) ||
                        [];
                    if (resultedOutcomeIds.length && isInitialPageLoad.value) {
                        removeOutcomes(resultedOutcomeIds);
                        // IMHO... user should be informed that resulted events were deleted from his ticket but nothing is designed in Figma (delete if someone will complain)
                        useSazkaSnackbar().createSnackbar({
                            message: "Skončené příležitosti byly smazány z vašeho tiketu",
                            type: "success",
                            duration: 4000,
                            icon: "attention",
                        });
                        console.info(`Resulted outcomes (${resultedOutcomeIds}) were removed from ticket.`);
                    }
                    unwatch();
                }
            },
        );

        return {
            activeStoreNumber,
            errors,
            initialized,
            sendingInProgress,
            userAttemptedToUpdateTicketDuringSending,
            eventsFiltered,
            ticketType,
            ticketStake,
            originalOutcomes,
            ticketOutcomes,
            ticketOutcomeCount,
            bet,
            systemBets,
            kombiStakes,
            kombiStakesTotal,
            stake,
            potentialPayout,
            bonusPercent,
            bonus,
            possibleWin,
            warningFlags,
            isWarningRaised,
            isErrorRaised,
            availableKombiGroups,
            kombiGroupNumberByOutcomeId,
            availableKombiGroupsByOutcomeId,
            empty,
            placed,
            kombiGroupMapping,
            slipBuild,
            slipBet,
            eventIds,
            outcomeIds,
            optionDisplayMiniTicket,
            optionPriceChangeBehaviour,
            pinned,
            settingsOpen,
            collapsed,
            showSendingErrors,
            setActiveStoreNumber,
            setTicketType,
            getEventByOutcomeId,
            changeStake,
            setKombiGroupMapping,
            replaceKombiGroupMapping,
            isOutcomePresent,
            toggleOutcome,
            addOutcome,
            removeOutcome,
            removeOutcomes,
            sendTicket,
            clearTicket,
            clearSlipBet,
            setOptionDisplayMiniTicket,
            setOptionPriceChangeBehaviour,
            dismissError,
            addEventToBackup,
        };
    },
    {
        persist: {
            pick: ["optionDisplayMiniTicket", "optionPriceChangeBehaviour", "pinned", "settingsOpen", "collapsed"],
            storage: localStorage,
        },
    },
);

// Dynamically created store (count of stores is driven by MAX_TICKET_NUMBER) which is driven by "Ticket" store
const useTicketBaseStore = (i: number) =>
    defineStore(
        `Ticket${i}`,
        () => {
            const { setItem: setCookie, getItem: getCookie, removeItem: removeCookie } = useCookieStorage();
            const route = useRoute();
            const { isEmptyOutcome } = useUtils();
            // getEventsDrilldownDetail is used here instead of getEventsDrilldown because getEventsDrilldown does not return 'long-term events/outrights' (like overall league winner etc.)
            // This 'orchestration' ep is used here also to mitigate risk of data discrepancies between different endpoints... built on presumption that all 'orchestration' endpoints use the same data source.
            // Even if it means to load unnecessary data like drilldownNodes and collections.
            const { getEventsDrilldown, getEventsDrilldownDetail, postSlipBet, postSlipBuild } = useOpenbetApi();

            const initialized: Ref<boolean> = ref(false);
            const timestamp: Ref<number> = ref(0);
            const restorePreviousState: Ref<boolean> = ref(false);
            const initFromCookies = () => {
                restorePreviousState.value = Boolean(getCookie("ticket_restore_previous_state"));
            };
            const setupWatchers = () => {
                watchEffect(() => {
                    // Condition to set new cookie expiration whenever timestamp changes
                    if (timestamp.value) {
                        setCookie("ticket_restore_previous_state", "true", "essential", {
                            expires: new Date(Date.now() + RESTORE_EXPIRATION),
                        });
                    } else {
                        removeCookie("ticket_restore_previous_state");
                    }
                });
            };
            const init = () => {
                initFromCookies();
                setupWatchers();
                if (!restorePreviousState.value) {
                    watch(
                        initialized,
                        (newValue) => {
                            if (newValue) clearTicket(true);
                        },
                        { once: true },
                    );
                }
                if (isDevelopmentEnv()) {
                    console.info("ticket-store", "ticket_restore_previous_state", restorePreviousState.value);
                }
            };
            init();

            // state
            const fetchInterval: Ref<number> = ref(0);
            const sendingInProgress: Ref<TicketSending | undefined> = ref(undefined);
            const ticketType: Ref<TicketType> = ref(TicketType.SOLO); // persisted
            const ticketStakeLastBet: Ref<number> = ref(); // persisted
            const ticketStake: Ref<number> = ref(ticketStakeLastBet.value || TICKET_STAKE); // persisted
            const kombiStakes: Ref<number[]> = ref([]); // persisted
            const simplifiedOutcomes: Ref<SimplifiedOutcome[]> = ref([]); // persisted
            const outcomes: Ref<Array<OutcomeMarket | EventsDrilldownDetailEventMarketOutcome>> = ref([]); // persisted
            const kombiGroupMapping: Ref<KombiGroupMapping> = ref({}); // persisted
            const legs: ShallowRef<Leg[]> = shallowRef([]); // persisted
            const slipBuild: Ref<SlipBuild> = ref({} as SlipBuild); // persisted
            // Needs to be initialized as empty object to keep reactivity functional in warningFlags computedAsync
            const slipBet: Ref<SlipBet> = ref({} as SlipBet);
            const events: Ref<EventDetail[]> = ref([]);
            // No endpoint returns event which is over, and we need to 'backup' (cache) these events, so we can display events on ticket even when the event is over
            const eventsBackup: Ref<EventDetail[]> = ref([]); // persisted
            const eventIdsOrder: Ref<string[]> = ref([...(simplifiedOutcomes.value?.map((so) => so.eventId) || [])]);

            // getters
            const eventIds: ComputedRef<string[]> = computed(() =>
                Array.from(new Set(simplifiedOutcomes.value.map((so) => so.eventId))),
            );
            const marketIds: ComputedRef<string[]> = computed(() =>
                Array.from(new Set(simplifiedOutcomes.value.map((so) => so.marketId))),
            );
            const outcomeIds: ComputedRef<string[]> = computed(() =>
                Array.from(new Set(simplifiedOutcomes.value.map((so) => so.id))),
            );
            const eventsDetail: Ref<EventDetail[]> = computedAsync(
                async () => {
                    // 'timestamp' evaluation is here to trigger fresh data fetch by getEventsDrilldownDetail.
                    // Without that it would recalculate only on store initialization and on eventIds change,
                    // but we need to get fresh data periodically as well.
                    if (!marketIds.value?.length || !timestamp.value) return !timestamp.value ? undefined : [];
                    const response = await getEventsDrilldownDetail(
                        { marketIds: marketIds.value },
                        { wsData: { update: true, stateNameOverride: StateEnum.EventDrilldownDetailTicket } },
                    );
                    return response?.length
                        ? (response.map((e) =>
                              _omit(e, ["drilldownNodes", "orderedOutcomes", "oddsByHeaders"]),
                          ) as EventDetail[])
                        : [];
                },
                undefined, // Do not change the default initialState to empty array otherwise we won't be able to detect the store initialization
                { lazy: true },
            );
            const isEveryOutcomePresentInEvent = (event: EventDetail): boolean =>
                simplifiedOutcomes.value
                    .filter((so) => so.eventId === event.id)
                    ?.map((so) => so.id)
                    ?.every((id) =>
                        event.markets
                            ?.flatMap((m) => m.outcomes)
                            .flatMap((o) => o.id)
                            .includes(id),
                    ) || false;
            watch(eventsDetail, (newValue) => {
                // Need to do a 'backup' here because events which are over/resulted are not returned by the endpoint or sometimes happen that markets/outcomes are incomplete or missing completely :(
                const newEventsBackup: EventDetail[] = _cloneDeep(eventsBackup.value);
                const newResponse: EventDetail[] = [];
                newValue?.forEach((ed) => {
                    const foundEventIdx = newEventsBackup.findIndex((e) => ed.id === e.id);
                    // Seems that sometimes if the event is over (should be marked as resulted as well... but don't rely on that) and not already disappeared from ep, not all outcomes/markets are returned hence we need to maintain that backup will NOT be overwritten by ishtty data
                    let outcomeIdDiff: string[] = [];
                    const outcomeIdsNew = ed.markets?.flatMap((m) => m.outcomes)?.flatMap((o) => o.id);
                    if (foundEventIdx > -1) {
                        const outcomeIdsBackup = newEventsBackup[foundEventIdx].markets
                            ?.flatMap((m) => m.outcomes)
                            ?.flatMap((o) => o.id);
                        outcomeIdDiff = _xor(outcomeIdsNew, outcomeIdsBackup);
                        if (outcomeIdDiff.length && outcomeIdsNew?.length) {
                            // Iterate from the end of array and keep only the most up-to-date backup at the top
                            for (let i = newEventsBackup.length; i--; ) {
                                if (newEventsBackup[i].id === ed.id && i !== foundEventIdx) {
                                    newEventsBackup.splice(i, 1);
                                }
                            }
                        }
                    }
                    if (
                        ((foundEventIdx === -1 && outcomeIdsNew?.length) ||
                            (outcomeIdDiff.length && outcomeIdsNew?.length)) &&
                        isEveryOutcomePresentInEvent(ed)
                    ) {
                        newEventsBackup.unshift(ed);
                    }
                    // BEWARE: Initial data load can contain ishtty data... What we can do with it? Nothing, just wait for next data load and pray. :(
                    newResponse.unshift(ed);
                });
                eventsBackup.value = newEventsBackup;
                events.value = newResponse;
            });
            // We combine here events returned by endpoint and missing from backup, ordered by simplifiedOutcomes to maintain order in which it was added to ticket
            const eventsAll: ComputedRef<EventDetail[]> = computed(() => {
                const filteredEvents = events.value.filter((e) => isEveryOutcomePresentInEvent(e));
                const filteredEventsBackup = eventsBackup.value.filter((e) => isEveryOutcomePresentInEvent(e));
                const union: EventDetail[] = _unionBy(filteredEvents, filteredEventsBackup, "id");
                return _orderBy(union, (e) => -[...eventIdsOrder.value].reverse().indexOf(e.id));
            });
            const actualEvents: ComputedRef<EventDetail[]> = computed(() =>
                eventsAll.value?.map((e) => ({
                    ...e,
                    markets: e.markets
                        .map((m) => ({
                            ...m,
                            outcomes: m.outcomes.filter((o) => outcomeIds.value.includes(o.id)),
                        }))
                        .filter((m) => m.outcomes.length > 0),
                })),
            );
            const actualMarkets: ComputedRef<Market[]> = computed(() =>
                _uniqBy(
                    eventsAll.value
                        ?.flatMap((event) => event.markets)
                        .filter((market) => marketIds.value.includes(market.id)),
                    "id",
                ),
            );
            const actualOutcomes: ComputedRef<Outcome[]> = computed(() =>
                _uniqBy(
                    eventsAll.value
                        ?.flatMap((event) => event.markets)
                        ?.flatMap((market) => market.outcomes)
                        .filter((outcome) => outcomeIds.value.includes(outcome.id)),
                    "id",
                ),
            );

            // Helper function used in warningFlags only (doesn't make sense to export)
            const generateFlagsFromResponseErrors = (
                flags: WarningFlag[],
                target: "ticket" | "bet",
                response: SlipBuild | SlipBet,
            ) => {
                response?.errors?.forEach((err) => {
                    const docRef: string[] = err.documentRef?.split(".");
                    let outcomeId = docRef?.[2];
                    let documentId = docRef?.[1];
                    let betId = docRef?.[0];
                    // Sometimes happen that err.documentRef is only "LEGx" seems that this happens only when there are nonCombinableLegGroups (but there can be more cases)
                    if (docRef?.length === 1 && docRef[0].startsWith("LEG")) {
                        const legs = response?.data?.legs;
                        outcomeId = legs?.find((l) => l.documentId === docRef[0])?.outcomes?.[0]?.outcomeRef;
                        documentId = docRef[0];
                        betId = undefined;
                    }
                    const eventId = outcomeId ? getEventIdByOutcomeId(outcomeId) : undefined;
                    // BEWARE: Outcomes in events which are over/resulted or started (started events are also marked with 'EVENT_STARTED' err msg) are declared as "OUTCOME_SUSPENDED" as well as suspended outcomes
                    setFlag(
                        flags,
                        target,
                        (err?.id as WarningFlagType) || "UNKNOWN_ERROR",
                        docRef ? [outcomeId, documentId, betId, eventId] : [],
                        err?.text,
                    );
                });
            };

            // This is here mainly because of crappy api which gives us ishtty data and to catch errors during slip build.
            // There are various error states for different parts of the ticket (event, outcome, leg, ...) which creates warning flags.
            const warningFlags: Ref<WarningFlag[]> = computedAsync(
                async () => {
                    const flags: WarningFlag[] = [];
                    if (!initialized.value) {
                        return flags;
                    }
                    if (events.value && events.value.length !== eventIds.value?.length) {
                        flags.push({ target: "event", type: "length" });
                    }

                    const eventOutcomes =
                        events.value
                            ?.flatMap((e) => e.markets)
                            ?.flatMap((m) => m.outcomes)
                            ?.filter((o) => outcomeIds.value.includes(o.id)) || [];
                    if (eventOutcomes?.length !== outcomeIds.value?.length) {
                        flags.push({ target: "outcome", type: "length" });
                    }

                    // If event/outcome is not returned from api most probably it finished already... or it is temporarily unavailable for unknown reason
                    const eventMissingIds = _xor(
                        eventIds.value,
                        events.value?.length > 0 ? events.value.map((e) => e.id) : [],
                    );
                    if (eventMissingIds.length > 0) {
                        const response = await getEventsDrilldown({ eventIds: eventMissingIds });
                        flags.push({
                            target: "event",
                            type: "missingIds",
                            info:
                                response?.length > 0
                                    ? eventMissingIds.filter((id) => !response.map((e) => e.id).includes(id))
                                    : eventMissingIds,
                        });
                    }
                    const outcomeMissingIds = _xor(
                        outcomeIds.value,
                        eventOutcomes?.length > 0 ? eventOutcomes.map((o) => o.id) : [],
                    );
                    if (outcomeMissingIds.length > 0) {
                        flags.push({ target: "outcome", type: "missingIds", info: outcomeMissingIds });
                    }

                    if (events.value?.length > 0) {
                        const eventNoMarkets: string[] = [];
                        const outcomeSuspended: string[] = [];
                        const eventSuspended: string[] = [];
                        events.value.forEach((e) => {
                            if (!e.markets?.length) {
                                eventNoMarkets.push(e.id);
                            } else {
                                e.markets
                                    .flatMap((m) => m.outcomes)
                                    .filter((o) => outcomeIds.value?.includes(o.id))
                                    .forEach((o) => {
                                        if (o.status === Status.Suspended || !o.active) {
                                            outcomeSuspended.push(o.id);
                                        }
                                    });
                            }
                            if (e.status === Status.Suspended || !e.active) {
                                eventSuspended.push(e.id);
                            }
                        });
                        // Happens when there is some unexpected situation (like weather condition prevents continuation of the match)
                        if (eventNoMarkets.length > 0)
                            flags.push({ target: "event", type: "noMarkets", info: eventNoMarkets });
                        if (outcomeSuspended.length > 0)
                            flags.push({ target: "outcome", type: "suspended", info: outcomeSuspended });
                        if (eventSuspended.length > 0)
                            flags.push({ target: "event", type: "suspended", info: eventSuspended });
                    }

                    generateFlagsFromResponseErrors(flags, "ticket", slipBuild.value);
                    generateFlagsFromResponseErrors(flags, "bet", slipBet.value);

                    if (slipBuild.value?.data?.nonCombinableLegGroups) {
                        const legs = slipBuild.value?.data?.legs;
                        const info = slipBuild.value?.data.nonCombinableLegGroups?.[0].map((i) => {
                            const outcomeId = legs?.find((l) => l.documentId === i)?.outcomes?.[0]?.outcomeRef;
                            const documentId = i;
                            const betId = undefined;
                            const eventId = getEventIdByOutcomeId(outcomeId);
                            return [outcomeId, documentId, betId, eventId];
                        });
                        flags.push({
                            target: "leg",
                            type: "nonCombinableLegGroups",
                            info,
                        });
                    }

                    return flags;
                },
                [],
                { lazy: false, shallow: false },
            );
            const isWarningRaised: ComputedRef<boolean> = computed(() => warningFlags.value.length > 0);
            const availableKombiGroups: ComputedRef<KombiGroup[]> = computed(() => {
                const retKombiGroups = [KombiGroup.T];
                const currGroupNo = Object.keys(kombiGroupMapping.value).filter(
                    (k) => k !== KombiGroup.T.toString(),
                ).length;
                const maxGroupNo =
                    !AVAILABLE_KOMBIGROUP_ALL && currGroupNo < MAX_KOMBIGROUP_NUMBER
                        ? currGroupNo + 1
                        : MAX_KOMBIGROUP_NUMBER;
                for (let i = 1; i <= maxGroupNo; i++) {
                    retKombiGroups.push(KombiGroup[KombiGroup[i]]);
                }
                return retKombiGroups;
            });
            const kombiGroupNumberByOutcomeId: ComputedRef<(outcomeId: string) => number> = computed(
                () => (outcomeId: string) => {
                    for (const groupNumber in kombiGroupMapping.value) {
                        if (kombiGroupMapping.value[groupNumber].includes(outcomeId)) {
                            return Number(groupNumber);
                        }
                    }
                },
            );
            const availableKombiGroupsByOutcomeId: ComputedRef<(outcomeId: string) => KombiGroup[]> = computed(
                () => (outcomeId: string) => {
                    const groupNumber = kombiGroupNumberByOutcomeId.value(outcomeId);
                    const outcomeCount = kombiGroupMapping.value[groupNumber]?.length || 0;
                    const newAvailableKombiGroups = _cloneDeep(availableKombiGroups.value);
                    // Makes no sense to offer movement to new (empty) group if current group is last and consist only one outcome
                    if (
                        outcomeCount === 1 &&
                        newAvailableKombiGroups.at(-2) === groupNumber &&
                        !kombiGroupMapping.value[newAvailableKombiGroups.at(-1)]?.length
                    ) {
                        newAvailableKombiGroups.pop();
                    }
                    return newAvailableKombiGroups;
                },
            );
            const empty: ComputedRef<boolean> = computed(
                () => !initialized.value || !slipBuild.value?.data?.outcomes?.length,
            );
            const placed: ComputedRef<boolean> = computed(
                () => initialized.value && slipBet.value?.data?.status === SlipBetStatus.Placed,
            );

            // actions
            const waitUntilInitialized = async (time: number = 100, timeout: number = 10000) => {
                await waitUntilTrue(initialized, time, timeout).catch(() => {
                    logException(`ticket${i}`, undefined, {
                        message: `Store initialization timed out. Timeout interval ${timeout} breached.`,
                    });
                });
            };

            const setFetchInterval = (interval: number) => {
                fetchInterval.value = interval;
            };
            const setTimestamp = () => {
                timestamp.value = performance.now();
            };
            const setTicketType = (type: TicketType) => {
                ticketType.value = type;
            };

            const getEventIdByOutcomeId = (id: string): string =>
                simplifiedOutcomes.value?.find((so) => so.id === id)?.eventId;
            const getEventByOutcomeId = (id: string): EventDetail =>
                eventsAll.value.find((evt) => evt.id === getEventIdByOutcomeId(id));
            const isOutcomePresent = (outcome: OutcomeMarket | EmptyMarket | EventsDrilldownDetailEventMarketOutcome) =>
                simplifiedOutcomes.value.some((so) => so.id === outcome.id);

            const toggleOutcome = (
                outcome: OutcomeMarket | EmptyMarket | EventsDrilldownDetailEventMarketOutcome,
                betPlacement: BetPlacement,
                betCategory: BetCategory,
                singleOutcomePerEvent: boolean = true,
            ) => {
                // For better user-friendliness we want to keep the order of events as they were added to ticket
                if (!eventIdsOrder.value?.includes(outcome.parentMarket.eventId)) {
                    eventIdsOrder.value = [
                        ...simplifiedOutcomes.value.map((so) => so.eventId),
                        outcome.parentMarket.eventId,
                    ];
                } else if (isOutcomePresent(outcome)) {
                    eventIdsOrder.value = eventIdsOrder.value.filter((id) => id !== outcome.parentMarket.eventId);
                }

                if (simplifiedOutcomes.value?.length === MAX_LEG_NUMBER && !isOutcomePresent(outcome)) {
                    const lastOutcome = outcomes.value.at(-1);
                    const isOutcomeParentMarketAlreadyInTicket = outcomes.value.find(
                        (o) => o.parentMarket?.eventId === outcome.parentMarket?.eventId,
                    );
                    if (lastOutcome.id !== outcome.id) {
                        if (!isOutcomeParentMarketAlreadyInTicket) {
                            useSazkaSnackbar().createSnackbar({
                                message: "Na tiketu můžete mít maximálně 30 příležitostí",
                                type: "success",
                                duration: 4000,
                                icon: "attention",
                            });
                            return;
                        }
                    }
                }

                if (singleOutcomePerEvent) {
                    const soForRemoval = simplifiedOutcomes.value.filter(
                        (so) => so.eventId === outcome.parentMarket.eventId,
                    );
                    if (soForRemoval.length === 1 && soForRemoval[0].id !== outcome.id) {
                        replaceOutcome(soForRemoval[0], outcome);
                        return;
                    } else {
                        soForRemoval.forEach((so) => {
                            if (so.id !== outcome.id) removeOutcome(so.id);
                        });
                    }
                }
                if (!isOutcomePresent(outcome)) {
                    addOutcome(outcome, betPlacement, betCategory, singleOutcomePerEvent);
                } else {
                    removeOutcome(outcome.id);
                }
            };

            const addOutcome = async (
                outcome: OutcomeMarket | EmptyMarket | EventsDrilldownDetailEventMarketOutcome,
                betPlacement: BetPlacement,
                betCategory: BetCategory,
                singleOutcomePerEvent: boolean = true, // There is rule that we can place only one outcome/market per event at this moment
            ) => {
                // It shouldn't happen that 'empty market' will appear here... the name of the interface should rather be EmptyOutcome
                if (isEmptyOutcome(outcome)) {
                    return;
                }
                // Clear info about bet which was previously sent
                if (!_isEmpty(slipBet.value)) clearSlipBet();
                if (!isOutcomePresent(outcome)) {
                    simplifiedOutcomes.value = [
                        ...simplifiedOutcomes.value,
                        { id: outcome.id, marketId: outcome.marketId, eventId: outcome.parentMarket?.eventId },
                    ];
                    addOutcomeToKombiGroupMapping(outcome.id);
                } else if (!singleOutcomePerEvent) {
                    addOutcomeToKombiGroupMapping(outcome.id);
                }
                if (!outcomes.value.some((o) => o.id === outcome.id)) {
                    const newOutcome = _cloneDeep(outcome);
                    newOutcome.betPlacement = betPlacement;
                    newOutcome.betCategory = betCategory;
                    outcomes.value = [...outcomes.value, newOutcome];

                    setTimeout(async () => {
                        try {
                            await waitForEvent(outcome?.parentMarket?.eventId);
                        } catch (error) {
                            console.error(error.message);
                            removeOutcome(outcome.id);
                        } finally {
                            const event = getEventByOutcomeId(outcome.id);
                            newOutcome.betType = event?.markets?.find((market) => market.id === outcome.marketId)?.name;
                            await useAnalyticsUtils().setTicketBetsGeneral();
                            await useAnalyticsUtils().setTicketGeneral();
                            const ticketBet = await useAnalyticsUtils().getTicketBet(newOutcome);
                            analytics.sendAddBetToTicket(ticketBet);
                        }
                    }, 2000);
                }
                buildSlipForce.value = true;
                buildLegs();
            };

            function waitForEvent(eventId: string, timeout = 5000, interval = 100): Promise<EventDetail> {
                return new Promise((resolve, reject) => {
                    const startTime = Date.now();

                    const checkEvent = () => {
                        const event = eventsAll.value.find((event) => event.id === eventId);
                        if (event) {
                            resolve(event);
                        } else if (Date.now() - startTime >= timeout) {
                            reject(new Error(`Event with ID ${eventId} not found within timeout`));
                        } else {
                            setTimeout(checkEvent, interval);
                        }
                    };

                    checkEvent();
                });
            }

            const removeOutcome = async (
                outcomeId: string,
                singleOutcomePerEvent: boolean = true,
                groupNumber?: KombiGroup,
            ) => {
                // First, capture all necessary data BEFORE modifying state
                const savedOutcome = outcomes.value.find((o) => o.id === outcomeId);
                const event = getEventByOutcomeId(outcomeId);

                const watchOutcomesUnwatch = watch(
                    slipBuild,
                    async () => {
                        const { getLeagueById } = useDrilldownNodesStore();
                        const league = await getLeagueById(event?.competitionDrilldownTagId);
                        await useAnalyticsUtils().setTicketBetsGeneral();
                        await useAnalyticsUtils().setTicketGeneral();

                        const analyticsBet = {
                            label: event?.liveNow ? BetLabel.Live : BetLabel.Prematch,
                            group: ticketType.value === TicketType.KOMBI ? KombiGroup[groupNumber] : undefined,
                            gameCode: league?.code,
                            sportLeague: league?.name,
                            matchName: event?.name,
                            quote: savedOutcome?.prices[0]?.decimal.toString(),
                            betPlacement: savedOutcome.betPlacement,
                            betType: event?.markets?.find((market) => market.id === savedOutcome.marketId)?.name,
                            betCategory: savedOutcome.betCategory,
                        };

                        analytics.sendRemoveBetFromTicket(analyticsBet);

                        watchOutcomesUnwatch();
                    },
                    { once: true },
                );

                // Now perform all removal operations
                if (!singleOutcomePerEvent && groupNumber) {
                    // Remove event from backup
                    const sos = simplifiedOutcomes.value.filter((so) => so.id === outcomeId);
                    if (sos?.length === 1) {
                        eventsBackup.value = eventsBackup.value.filter((eb) => eb.id !== sos[0].eventId);
                    }
                    const outcomeCount = Object.keys(kombiGroupMapping.value)
                        .map((k) => kombiGroupMapping.value[k].includes(outcomeId))
                        .filter((b) => b).length;
                    if (outcomeCount <= 1) {
                        simplifiedOutcomes.value = simplifiedOutcomes.value.filter((so) => so.id !== outcomeId);
                    }
                    removeOutcomeFromKombiGroupMapping(outcomeId, groupNumber);
                } else {
                    // Remove event from backup
                    const soEventId = simplifiedOutcomes.value.find((so) => so.id === outcomeId)?.eventId;
                    eventsBackup.value = eventsBackup.value.filter((eb) => eb.id !== soEventId);
                    simplifiedOutcomes.value = simplifiedOutcomes.value.filter((so) => so.id !== outcomeId);
                    removeOutcomeFromKombiGroupMapping(outcomeId);
                }
                outcomes.value = outcomes.value.filter((o) => o.id !== outcomeId);

                // Trigger build operations
                buildSlipForce.value = true;
                buildLegs();
            };

            const replaceOutcome = (
                oldOutcomeRef: SimplifiedOutcome,
                newOutcome: OutcomeMarket | EmptyMarket | EventsDrilldownDetailEventMarketOutcome,
            ) => {
                // Need to create unreferenced copy here otherwise would not work once removed from simplifiedOutcomes
                const oldOutcome = _cloneDeep(oldOutcomeRef);
                // It shouldn't happen that 'empty market' will appear here... the name of the interface should rather be EmptyOutcome
                if (isEmptyOutcome(newOutcome)) {
                    return;
                }
                const so = simplifiedOutcomes.value.find((so) => so.eventId === oldOutcome.eventId);
                if (so) {
                    so.id = newOutcome.id;
                    so.marketId = newOutcome.marketId;
                }
                const groupNumber = kombiGroupNumberByOutcomeId.value(oldOutcome.id);
                if (groupNumber) {
                    kombiGroupMapping.value[groupNumber] = kombiGroupMapping.value[groupNumber].map((item) =>
                        item === oldOutcome.id ? newOutcome.id : item,
                    );
                }
                const index = outcomes.value.findIndex((o) => o.id === oldOutcome.id);
                if (index > -1) {
                    outcomes.value[index] = _cloneDeep(newOutcome);
                }
                buildSlipForce.value = true;
                buildLegs();
            };

            const changeStake = (amount: number, groupNumber?: number) => {
                if (_isNumber(groupNumber)) {
                    kombiStakes.value[groupNumber] = amount;
                } else if (amount != null) {
                    ticketStake.value = amount;
                }
            };

            const removeKombiStakeByRemovedGroupNumber = (
                kombiGroupMappingNew: KombiGroupMapping,
                kombiGroupMappingOld: KombiGroupMapping,
            ) => {
                const removedGroupNumber = _differenceWith(
                    Object.keys(kombiGroupMappingOld),
                    Object.keys(kombiGroupMappingNew),
                );
                if (removedGroupNumber?.[0] && removedGroupNumber[0] !== KombiGroup.T.toString()) {
                    kombiStakes.value.splice(Number(removedGroupNumber[0]), 1);
                }
            };
            const addOutcomeToKombiGroupMapping = (outcomeId: string) => {
                const newMapping = _cloneDeep(kombiGroupMapping.value);
                const currGroupNumber = Object.keys(kombiGroupMapping.value).filter(
                    (k) => k !== KombiGroup.T.toString(),
                ).length;
                const newGroupNumber =
                    currGroupNumber < MAX_KOMBIGROUP_NUMBER ? currGroupNumber + 1 : MAX_KOMBIGROUP_NUMBER;
                newMapping[newGroupNumber] = [...(newMapping[newGroupNumber] || []), outcomeId];
                // Add 'placeholder' for stake price
                if (Object.keys(newMapping).length > Object.keys(kombiGroupMapping.value).length) {
                    kombiStakes.value[newGroupNumber] = null;
                }
                kombiGroupMapping.value = newMapping;
            };
            const removeOutcomeFromKombiGroupMapping = (outcomeId: string, groupNumber?: KombiGroup) => {
                let newMapping = _cloneDeep(kombiGroupMapping.value);
                newMapping = removeMapping(newMapping, outcomeId, groupNumber);
                // Remove already entered bet price for specific group if there is no outcome left
                if (Object.keys(newMapping).length < Object.keys(kombiGroupMapping.value).length) {
                    removeKombiStakeByRemovedGroupNumber(newMapping, kombiGroupMapping.value);
                }
                kombiGroupMapping.value = repairKombiGroupNumberMappingConsistency(newMapping);
            };
            const setKombiGroupMapping = (outcomeId: string, groupNumber: KombiGroup) => {
                let newMapping = _cloneDeep(kombiGroupMapping.value);
                newMapping = removeMapping(newMapping, outcomeId);
                newMapping = addMapping(newMapping, outcomeId, groupNumber, false);
                // Remove already entered bet price for specific group if there is no outcome left and T group is set instead
                if (
                    (groupNumber === KombiGroup.T &&
                        Object.keys(newMapping).length <= Object.keys(kombiGroupMapping.value).length) ||
                    Object.keys(newMapping).length < Object.keys(kombiGroupMapping.value).length
                ) {
                    removeKombiStakeByRemovedGroupNumber(newMapping, kombiGroupMapping.value);
                }
                kombiGroupMapping.value = repairKombiGroupNumberMappingConsistency(newMapping);
            };
            // Use with caution and prefer setKombiGroupMapping if possible... this is here just because of reason we need to overcome limitation during creation of similar (kombi) ticket
            const replaceKombiGroupMapping = (mapping: KombiGroupMapping) => {
                kombiGroupMapping.value = _cloneDeep(mapping);
            };
            const buildLegs = () => {
                const newLegs: Leg[] = [];
                if (ticketType.value === TicketType.KOMBI) {
                    Object.keys(kombiGroupMapping.value).forEach((groupNumber) => {
                        newLegs.push({
                            documentId: "LEG" + newLegs.length,
                            outcomes: kombiGroupMapping.value[groupNumber].map((outcomeId: string) => ({
                                outcomeRef: outcomeId,
                            })),
                            options: {
                                banker: groupNumber === "0",
                                handicaps: kombiGroupMapping.value[groupNumber].map((outcomeId: string) => ({
                                    outcomeRef: outcomeId,
                                })),
                                inMultiples: false,
                                // TODO: /buildSlip ep throws "BET_PRICE_CHANGED" error for anything we put to prices (when there are multiple outcomes)... mobile app does not send prices (we'll copy this behavior for now).
                                //  But this means that it's not evaluated for price changes.
                                // prices: kombiGroupMapping.value[groupNumber].map((outcomeId: string) => ({
                                //     denominator: outcomes.value.find((outcome) => outcome.id === outcomeId)?.prices[0]
                                //         .denominator,
                                //     numerator: outcomes.value.find((outcome) => outcome.id === outcomeId)?.prices[0]
                                //         .numerator,
                                //     priceTypeRef: "LP",
                                //     winPlaceRef: "WIN",
                                // })),
                                winPlace: "WIN",
                                combinationRef: "BUILD_YOUR_OWN_BET",
                                group: groupNumber === "0" ? "B" : groupNumber,
                            },
                        });
                    });
                } else {
                    outcomes.value?.forEach((outcome) => {
                        newLegs.push({
                            documentId: "LEG" + newLegs.length,
                            outcomes: [{ outcomeRef: outcome.id }],
                            options: {
                                banker: false,
                                handicaps: [{ outcomeRef: outcome.id }],
                                inMultiples: true,
                                prices: [
                                    {
                                        denominator: outcome?.prices[0].denominator,
                                        numerator: outcome?.prices[0].numerator,
                                        priceTypeRef: "LP",
                                        winPlaceRef: "WIN",
                                    },
                                ],
                                winPlace: "WIN",
                            },
                        });
                    });
                }
                legs.value = newLegs;
            };
            const buildSlip = async (force: boolean = false) => {
                if (force) buildSlipForce.value = true;
                await buildSlipTrigger();
            };

            const evaluateSlipBetStatus = async (bet: SlipBet) => {
                switch (bet?.data?.status) {
                    case SlipBetStatus.Placed:
                        slipBet.value = bet;
                        return true;
                    case SlipBetStatus.ReferralPending:
                        sendingInProgress.value = TicketSending.Longer;
                    // eslint-disable-next-line no-fallthrough
                    case SlipBetStatus.BirDelay:
                        if (bet?.data?.poll && bet?.data?.pollPeriod) {
                            await wait(bet.data.pollPeriod * 1000);
                            if (bet?.actions?.[0]?.actionTypeRef === "SLIP_RETRIEVE" && bet?.actions?.[0]?.href) {
                                const authStore = useAuthStore();
                                // BEWARE: href is OB API... there is no appropriate alternative on SAG (/slips/{slipRef})
                                const slipResponse = await fetch(bet?.actions?.[0]?.href, {
                                    method: "GET",
                                    headers: {
                                        Authorization: `Bearer ${authStore.token.access_token}`,
                                        "Content-Type": "application/json",
                                    },
                                })
                                    .then((response) => response.json())
                                    .then((data) => data as SlipBet);
                                if (slipResponse) {
                                    return await evaluateSlipBetStatus(slipResponse);
                                }
                            }
                        }
                    // eslint-disable-next-line no-fallthrough
                    case SlipBetStatus.Failed:
                    default:
                        // Default is error
                        // Warning flags will be created automatically
                        slipBet.value = bet || ({} as SlipBet);
                        return false;
                }
            };

            const sendTicket = async (priceChangeBehaviour: PriceChangeBehaviour) => {
                const { showLogin } = useTokenAuth();
                await showLogin(route.fullPath, route.fullPath);
                if (sendingInProgress.value) {
                    return;
                }
                sendingInProgress.value = TicketSending.Regular;

                // these values gets rewritten somewhere along this function, so we need to save it here
                const analyticsType = ticketType.value;

                const bet = slipBuild.value?.data?.bets?.[0];
                const systemBets = bet?.systemBets;
                const kombiStakesTotal = systemBets?.reduce((count: number, sb, idx) => {
                    return count + (sb.lines * kombiStakes.value?.[idx + 1] || 0);
                }, 0);
                const analyticsStake =
                    ticketType.value === TicketType.KOMBI ? kombiStakesTotal.toString() : ticketStake.value.toString();

                const stakes = ticketType.value === TicketType.KOMBI ? kombiStakes.value.slice(1) : [ticketStake.value];
                // Convert SlipBuild data object to SlipBetData
                const slipBetData = new SlipBetData(slipBuild.value.data, priceChangeBehaviour, stakes);
                const response = await postSlipBet(slipBetData);
                // if ticket contains live event(s) it'll be most probably BIR_DELAYed so we need to check response from following call
                const statEval = await evaluateSlipBetStatus(response);
                if (!statEval) {
                    sendingInProgress.value = undefined;
                    // Shouldn't happen that we'll get no error from /slips ep... this is just failsafe
                    if (!response && _isEmpty(slipBet.value)) {
                        slipBet.value = {
                            errors: [{ type: "UNKNOWN_ERROR", documentRef: null, id: null, text: null }],
                        } as SlipBet;
                    }
                    return;
                }
                // If we are here it means that ticket has been placed successfully
                analyticsTicketObj.id = slipBet.value?.data?.id;
                analytics.sendBetMoney(analyticsType, analyticsStake);
                // Add to favorite events if not already present
                const eventIds = slipBet.value?.data?.outcomes?.map((o) => o?.eventRef);
                if (eventIds?.length > 0) {
                    const favouritesStore = useFavouritesStore();
                    eventIds.forEach((eventId) => {
                        if (!favouritesStore.favouriteEvents.includes(eventId))
                            favouritesStore.toggleFavouriteEvent(eventId);
                    });
                }
                // Refresh bets store data (ticket count)
                const betsStore = useBetsStore();
                await betsStore.fetchAllBets(true);
                // Refresh balance
                const { refreshBalance } = useNgSession();
                await refreshBalance(true);
                sendingInProgress.value = undefined;
            };
            // Do not use if possible... this is here just because of reason we need to overcome limitation during creation of similar ticket
            const addEventToBackup = (evt: EventDetail) => {
                const newEventsBackup: EventDetail[] = _cloneDeep(eventsBackup.value);
                newEventsBackup.unshift(evt);
                eventsBackup.value = newEventsBackup;
            };

            const clearTicket = (all: boolean = true) => {
                simplifiedOutcomes.value = [];
                kombiGroupMapping.value = {};
                slipBuild.value = {} as SlipBuild;
                legs.value = [];
                // We are mimicking here behavior of mobile app which leaves stake value always the last and doesn't clear to default
                // ticketStake.value = ticketStakeLastBet.value || TICKET_STAKE;
                kombiStakes.value = [];
                timestamp.value = 0;
                eventsBackup.value = [];
                outcomes.value = [];
                if (all) {
                    clearSlipBet();
                }
            };

            const clearSlipBet = () => {
                slipBet.value = {} as SlipBet;
            };

            // additional logic
            let fetchIntervalId = null;
            watch(
                fetchInterval,
                (newValue) => {
                    if (newValue) {
                        setTimestamp();
                        fetchIntervalId = setInterval(() => {
                            // Do not refresh data when we're sending the ticket
                            if (sendingInProgress.value) return;
                            setTimestamp();
                        }, fetchInterval.value);
                    } else {
                        if (fetchIntervalId) {
                            clearInterval(fetchIntervalId);
                        }
                        timestamp.value = 0;
                    }
                },
                { immediate: true },
            );

            // Change ticket type if count of outcomes changes (changes between ako and kombi are manual only)
            watch(outcomeIds, (newValue, oldValue) => {
                if (newValue.length > 1 && ticketType.value == TicketType.SOLO) {
                    ticketType.value = TicketType.AKO;
                } else if ((oldValue.length > 1 && newValue.length === 1) || newValue.length === 0) {
                    ticketType.value = TicketType.SOLO;
                }
            });

            // We need to add/remove groups when ticket type changes between ako <-> kombi and build the slip again
            watch(ticketType, async (newValue, oldValue) => {
                if (
                    (newValue === TicketType.AKO && oldValue === TicketType.KOMBI) ||
                    (newValue === TicketType.KOMBI && oldValue === TicketType.AKO)
                ) {
                    buildLegs();
                }
            });

            // Set fetch interval once there are some outcomes so timestamp is periodically set which triggers data fetch.
            // Complicated a bit but cannot figure out better way how to trigger data fetch in computedAsync.
            watch(
                outcomeIds,
                (newValue) => {
                    if (newValue.length === 0) {
                        setFetchInterval(0);
                        // Reset events backup when ticket is empty
                        eventsBackup.value = [];
                    } else if (newValue.length > 0 && fetchInterval.value === 0) {
                        setFetchInterval(FETCH_INTERVAL);
                    }
                },
                { immediate: true },
            );
            const buildSlipQueue = new PQueue({ concurrency: 1, interval: 1000, intervalCap: 1 });
            const buildSlipForce: Ref<boolean> = ref(false);
            const postSlipBuildPromise = async (legs: Leg[], ticketType: TicketType) =>
                await postSlipBuild(
                    { legs },
                    ticketType === TicketType.KOMBI ? { includePotentialPriceBreakdown: true } : undefined,
                ).then((response) => {
                    if (!response) return;
                    slipBuild.value = response;
                });
            const { trigger: buildSlipTrigger } = watchTriggerable(
                legs,
                async (newLegs, oldLegs) => {
                    if (
                        // Do not build the slip when we're sending the ticket
                        !sendingInProgress.value &&
                        newLegs?.length > 0 &&
                        (buildSlipForce.value ||
                            // Prevent triggering of the build during store initialization (when oldLegs is returned as undefined)
                            (Array.isArray(oldLegs) &&
                                (newLegs.length !== oldLegs.length ||
                                    // For kombi ticket we need to build slip whenever legs changes (buildLegs() is called even though legs are the same).
                                    // They do not contain prices, but we need to be able to calculate potential payout for various groups (include their mistake tolerance).
                                    ticketType.value === TicketType.KOMBI ||
                                    // On the other hand solo/ako contains prices, and we can react only on price changes.
                                    _differenceWith(newLegs, oldLegs, _isEqual).length > 0)))
                    ) {
                        if (buildSlipForce.value) buildSlipQueue.clear();
                        buildSlipForce.value = false;
                        await buildSlipQueue.add(() => postSlipBuildPromise(legs.value, ticketType.value));
                    } else if (!newLegs?.length) {
                        slipBuild.value = {} as SlipBuild;
                        buildSlipForce.value = false;
                    }
                },
                { immediate: true },
            );
            watch(timestamp, async () => {
                // Spread the load to reduce performance spikes
                await wait(3000);
                await buildSlipTrigger();
            });

            const actualOutcomesPrices = computed(() => actualOutcomes.value?.map((o) => o.prices?.[0]) || []);
            watchThrottled(
                actualOutcomesPrices,
                (newActualOutcomesPrices, oldActualOutcomesPrices) => {
                    if (!_differenceWith(newActualOutcomesPrices, oldActualOutcomesPrices, _isEqual).length) return;
                    outcomes.value?.forEach((o) => {
                        const actualOutcome = actualOutcomes.value.find((ao) => ao.id === o.id);
                        if (actualOutcome?.prices?.[0] && !_isEqual(o.prices[0], actualOutcome.prices[0])) {
                            o.prices[0] = { ...actualOutcome.prices[0] };
                        }
                    });
                    buildLegs();
                },
                {
                    throttle: 1000,
                    leading: false,
                    trailing: true,
                },
            );

            watch(
                [actualOutcomes, kombiGroupMapping],
                ([newActualOutcomes, newKombiGroupMapping], [oldActualOutcomes, oldKombiGroupMapping]) => {
                    if (
                        ticketType.value === TicketType.KOMBI &&
                        newActualOutcomes.length === oldActualOutcomes.length &&
                        !_isEqual(newKombiGroupMapping, oldKombiGroupMapping)
                    ) {
                        buildLegs();
                    }
                },
            );

            watch(
                slipBuild,
                (newValue) => {
                    if (newValue) {
                        initialized.value = true;
                    }
                },
                { once: true },
            );

            watch(placed, (newValue) => {
                if (newValue) {
                    // Store last solo/ako stake before clearing the ticket
                    if (ticketType.value !== TicketType.KOMBI) {
                        ticketStakeLastBet.value = ticketStake.value;
                    }
                    // slipBet object should contain all necessary data for further processing at this stage
                    clearTicket(false);
                }
            });

            watch(empty, (newValue, oldValue) => {
                if (newValue && !oldValue) {
                    // Clear the ticket include slipBet only when store is not initialized yet
                    clearTicket(!initialized.value);
                }
            });

            return {
                initialized,
                fetchInterval,
                timestamp,
                sendingInProgress,
                ticketType,
                ticketStake,
                ticketStakeLastBet,
                kombiStakes,
                simplifiedOutcomes,
                kombiGroupMapping,
                eventIds,
                events,
                eventsBackup,
                eventsAll,
                marketIds,
                actualMarkets,
                outcomeIds,
                outcomes,
                actualOutcomes,
                actualEvents,
                warningFlags,
                isWarningRaised,
                availableKombiGroups,
                kombiGroupNumberByOutcomeId,
                availableKombiGroupsByOutcomeId,
                empty,
                placed,
                legs,
                slipBuild,
                slipBet,
                waitUntilInitialized,
                setFetchInterval,
                setTicketType,
                getEventByOutcomeId,
                isOutcomePresent,
                toggleOutcome,
                addOutcome,
                removeOutcome,
                changeStake,
                setKombiGroupMapping,
                replaceKombiGroupMapping,
                buildSlip,
                sendTicket,
                addEventToBackup,
                clearTicket,
                clearSlipBet,
            };
        },
        {
            persist: {
                pick: [
                    "ticketType",
                    "ticketStake",
                    "ticketStakeLastBet",
                    "kombiStakes",
                    "simplifiedOutcomes",
                    "outcomes",
                    "kombiGroupMapping",
                    "legs",
                    "eventsBackup",
                    "slipBuild",
                ],
                storage: localStorage,
            },
        },
    );

// Enable HMR
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useTicketStore, import.meta.hot));
}
